import React, { memo, useRef, useEffect } from 'react';
import { EditorContent } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import { AppNode, NodeSize } from '@/types';
import { TextNodeResizeHandle } from '../TextNodeResizeHandle';
import { ParagraphIcon } from './ParagraphIcon';
import { useCtrlWheelZoom } from '../../hooks';

export const SelfWritePreviewView: React.FC<{
  data: AppNode;
  editor: Editor | null;
  onDoubleClick: () => void;
  onResize: (size: NodeSize) => void;
}> = memo(({ data, editor, onDoubleClick, onResize }) => {
  const textTrimmed = (data.data.textContent || '').trim();
  const hasRich = !!data.data.richContent &&
    (data.data.richContent as any).content?.some((c: any) => c.content?.length > 0 || c.text?.length > 0);
  const hasContent = textTrimmed.length > 0 || hasRich;
  const scrollRef = useRef<HTMLDivElement>(null);
  const handleCtrlWheel = useCtrlWheelZoom();

  // 阻止中键触发浏览器自动滚动（autoscroll），只保留画布平移
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 1) {
        e.preventDefault(); // 阻止浏览器 autoscroll
      }
    };

    el.addEventListener('mousedown', handleMouseDown);
    return () => el.removeEventListener('mousedown', handleMouseDown);
  }, []);

  return (
    <div
      className="relative w-full h-full"
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick(); }}
    >
      {hasContent && editor ? (
        <div
          ref={scrollRef}
          className="overflow-y-auto rich-text-scroll nowheel"
          onWheel={handleCtrlWheel}
          style={{ width: 'calc(100% - 27px)', height: 'calc(100% - 40px)', transform: 'translate(18px, 20px)' }}
        >
          <div className="w-full h-full flex justify-center items-center pr-3">
            <EditorContent editor={editor} className="w-full h-full rich-text-editor pointer-events-none" />
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-4 w-full h-full">
          <span className="text-sm text-white/40 font-medium">
            请编写内容，开始你的创作。
          </span>
          <ParagraphIcon />
        </div>
      )}
      <TextNodeResizeHandle onResize={onResize} />
    </div>
  );
});
